import { useEffect, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";

export type Metric = {
  value: number;
  prefix?: string;
  suffix?: string;
  label: string;
  proof?: string;
  /** Decimals to render. Default 0 (or 1 if value < 10 and non-integer). */
  decimals?: number;
};

function useInView<T extends HTMLElement>(threshold = 0.4) {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setSeen(true);
            io.disconnect();
          }
        });
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return { ref, seen };
}

function Counter({
  to,
  prefix = "",
  suffix = "",
  decimals,
  duration = 1500,
}: {
  to: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  duration?: number;
}) {
  const { ref, seen } = useInView<HTMLSpanElement>(0.4);
  // Start at the real value so SSR / no-JS / AI crawlers read the true number,
  // not "0". The count-up animation runs on top of it, client-side only.
  const [n, setN] = useState(to);
  useEffect(() => {
    if (!seen) return;
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      setN(to);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const k = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - k, 3);
      setN(to * eased);
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [seen, to, duration]);

  const d = decimals ?? (Number.isInteger(to) ? 0 : to < 10 ? 1 : 0);
  const display = to >= 1000 && d === 0 ? Math.floor(n).toLocaleString() : n.toFixed(d);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

export function MetricCounters({
  items,
  variant = "card",
}: {
  items: Metric[];
  variant?: "card" | "inline";
}) {
  if (variant === "inline") {
    return (
      <div className="grid grid-cols-3 gap-x-6 gap-y-3 max-w-2xl">
        {items.map((m) => (
          <div key={m.label}>
            <div className="text-2xl md:text-3xl font-semibold leading-none text-gradient">
              <Counter to={m.value} prefix={m.prefix} suffix={m.suffix} decimals={m.decimals} />
            </div>
            <p className="mt-2 text-xs text-white/70 leading-snug">{m.label}</p>
            {m.proof && (
              <p className="text-[10px] uppercase tracking-wider text-white/40 mt-0.5">{m.proof}</p>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="glass-card p-6 md:p-8">
      <div
        className={`grid gap-y-8 gap-x-4 divide-y lg:divide-y-0 lg:divide-x divide-white/10 ${
          items.length === 4
            ? "grid-cols-2 lg:grid-cols-4"
            : items.length === 3
              ? "grid-cols-1 sm:grid-cols-3"
              : "grid-cols-1 sm:grid-cols-2"
        }`}
      >
        {items.map((m, i) => (
          <div key={m.label} className={`px-2 lg:px-6 ${i >= 2 ? "pt-8 lg:pt-0" : ""}`}>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-none text-gradient">
                <Counter to={m.value} prefix={m.prefix} suffix={m.suffix} decimals={m.decimals} />
              </span>
              <ArrowUpRight className="size-4 text-pink-soft shrink-0" />
            </div>
            <p className="mt-3 text-sm font-medium text-white/90">{m.label}</p>
            {m.proof && (
              <p className="mt-1 text-[11px] uppercase tracking-wider text-white/45">{m.proof}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
